import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRolePerms } from '../features/auth/useRolePerms.js';
import { useAuth } from '../features/auth/AuthProvider.js';
import { ShareDocModal } from '../components/domain/ShareDocModal.js';
import { DocViewerModal } from '../components/domain/DocViewerModal.js';
import { WorkerSignModal } from '../components/domain/WorkerSignModal.js';
import { avEl } from '../lib/workerBadges.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '../lib/toast.js';

export interface SharedDocRecipient {
  id: string;
  workerId: string | null;
  recipientType?: 'worker' | 'contact' | 'supplier';
  workerName: string;
  workerEmail?: string | null;
  status: 'pending' | 'viewed' | 'signed' | 'declined';
  signedAt: string | null;
  notifiedAt?: string | null;
  viewedAt?: string | null;
  declineReason?: string | null;
  signatureImageUrl?: string | null;
  signatureData?: string | null;
  signedPdfUrl?: string | null;
  signedPdfSha256?: string | null;
  sealedAt?: string | null;
  timestamped?: boolean;
  verifiedVia?: string | null;
}

export interface SharedDoc {
  id: string;
  name: string;
  fileName?: string;
  url?: string | null;
  fileUrl?: string | null;
  mimeType?: string | null;
  createdAt?: string;
  sharedAt?: string;
  sharedBy?: string | null;
  createdByName?: string | null;
  createdByRole?: string | null;
  documentId?: string | null;
  projectId?: string | null;
  docType?: string | null;
  docRef?: string | null;
  docNotes?: string | null;
  message?: string | null;
  requireSignature?: boolean;
  recipients: SharedDocRecipient[];
}

interface WorkerLite {
  id: string;
  name: string;
  role: string;
  photo: string | null;
}

export function DocSign() {
  const { isInternal, isAdmin, isManager } = useRolePerms();
  const { user } = useAuth();
  const qc = useQueryClient();
  const remind = useMutation({
    mutationFn: ({ docId, recipientId }: { docId: string; recipientId: string }) => api.post<{ sent: boolean; link: string | null; email: string | null }>(`/shared-docs/${docId}/remind/${recipientId}`).then((r) => r.data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['shared-docs'] });
      if (res.sent) toast.success('Reminder sent', `Emailed ${res.email}`);
      else toast.warn('Email not sent', res.email ? 'SendGrid refused — copy the link and send it yourself.' : 'No email address on record — copy the link and send it yourself.');
    },
    onError: () => toast.error('Reminder failed'),
  });
  const copyLink = async (docId: string, recipientId: string) => {
    try {
      const res = await api.get<{ link: string }>(`/shared-docs/${docId}/link/${recipientId}`);
      await navigator.clipboard.writeText(res.data.link);
      toast.success('Link copied', 'Paste it into WhatsApp, SMS or an email.');
    } catch {
      toast.error('Could not copy the link');
    }
  };
  const typeLabel: Record<string, string> = { worker: 'Worker', contact: 'Client contact', supplier: 'Supplier' };
  const fmtWhen = (iso: string | null | undefined) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;
  const [activeId, setActiveId] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [viewer, setViewer] = useState<{ url: string; name: string; subtitle?: string } | null>(null);
  const [signOpen, setSignOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'signed'>('all');

  const { data: docs = [] } = useQuery({
    queryKey: ['shared-docs'],
    queryFn: () => api.get<SharedDoc[]>('/shared-docs').then((r) => r.data),
  });

  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<WorkerLite[]>('/workers').then((r) => r.data),
  });

  const active = activeId ? docs.find((d) => d.id === activeId) ?? null : null;
  const lookupWorker = (id: string) => workers.find((w) => w.id === id) ?? null;
  const q = search.trim().toLowerCase();
  const visibleDocs = docs.filter((d) => {
    const signed = d.recipients.filter((r) => r.status === 'signed').length;
    if (statusFilter === 'pending' && signed === d.recipients.length) return false;
    if (statusFilter === 'signed' && signed === 0) return false;
    if (!q) return true;
    const hay = [d.name, d.projectId ?? '', d.docType ?? '', d.createdByName ?? '', ...d.recipients.map((r) => (r.workerId ? lookupWorker(r.workerId)?.name : null) ?? r.workerName)].join(' ').toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="page" id="page-docsign">
      <div className="hbar">
        <div>
          <div className="ph">✍ Document Signing</div>
          <div className="ps">Share documents for worker signature — full audit trail</div>
        </div>
        {isInternal ? <button className="btn btn-primary" onClick={() => setShareOpen(true)}>+ Share Document</button> : null}
      </div>
      <div className="g2">
        <div className="card">
          <div className="sec-title" style={{ marginBottom: 10 }}>Shared Documents</div>
          {docs.length > 0 ? (
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input type="text" className="form-input" id="docsign-search" placeholder="Search document, worker, project…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 1 }} />
              <select className="form-input" id="docsign-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'all' | 'pending' | 'signed')} style={{ width: 130 }}>
                <option value="all">All</option>
                <option value="pending">Has pending</option>
                <option value="signed">Has signed</option>
              </select>
            </div>
          ) : null}
          <div id="shared-docs-list">
            {visibleDocs.length === 0 && docs.length > 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>No documents match</div>
            ) : docs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>✍</div>
                <div>No documents shared yet</div>
                {isInternal ? (
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={() => setShareOpen(true)}>+ Share a Document</button>
                ) : null}
              </div>
            ) : visibleDocs.map((d) => {
              const signed = d.recipients.filter((r) => r.status === 'signed').length;
              const pending = d.recipients.length - signed;
              const dateStr = new Date(d.sharedAt ?? d.createdAt ?? Date.now()).toLocaleDateString('en-GB');
              const sharedBy = d.createdByName ?? d.sharedBy ?? '—';
              return (
                <div key={d.id} className="sign-card" onClick={() => setActiveId(d.id)} style={{ cursor: 'pointer', borderColor: activeId === d.id ? 'var(--accent)' : undefined }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7 }}>
                    <div>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 13 }}>{d.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                        {d.projectId ? `${d.projectId} · ` : ''}Shared by {sharedBy} · {dateStr}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                      {signed > 0 ? <span className="sign-chip-signed">{signed} signed</span> : null}
                      {pending > 0 ? <span className="sign-chip-pending">{pending} pending</span> : null}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {d.recipients.map((r) => {
                      const isSigned = r.status === 'signed';
                      const w = r.workerId ? lookupWorker(r.workerId) : null;
                      const display = (w?.name ?? r.workerName).split(' ')[0] ?? r.workerName;
                      return (
                        <div
                          key={r.id}
                          title={w?.name ?? r.workerName}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            background: isSigned ? 'var(--live-dim)' : 'var(--warn-dim)',
                            border: `1px solid ${isSigned ? 'rgba(34,197,94,.3)' : 'rgba(245,158,11,.3)'}`,
                            borderRadius: 6,
                            padding: '2px 7px',
                            fontSize: 10,
                            color: isSigned ? 'var(--live)' : 'var(--warn)',
                          }}
                        >
                          {isSigned ? '✓' : r.status === 'declined' ? '✕' : r.status === 'viewed' ? '👁' : '⏱'} {display}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="card" id="doc-sig-detail-panel">
          {active ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 14 }}>{active.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                    {active.projectId ?? ''}{active.projectId && active.docType ? ' · ' : ''}{active.docType ?? ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {isAdmin || isManager ? <button className="btn btn-primary btn-sm" onClick={() => window.open(`/api/shared-docs/${active.id}/signed.pdf`, '_blank')}>⬇ PDF Report</button> : null}
                  {isAdmin || active.recipients.some((r) => r.workerId === user?.workerId && r.status !== 'signed') ? <button className="btn btn-ghost btn-sm" onClick={() => setSignOpen(true)}>✍ Sign Now</button> : null}
                  {active.url || active.fileUrl ? (
                    <button className="btn btn-ghost btn-sm" onClick={() => setViewer({ url: (active.url ?? active.fileUrl)!, name: active.name, subtitle: active.fileName ?? '' })}>📄 View</button>
                  ) : null}
                  {isAdmin || isManager ? <button className="btn btn-ghost btn-sm" onClick={() => window.open(`/api/shared-docs/${active.id}/audit.csv`, '_blank')}>⬇ Audit</button> : null}
                </div>
              </div>
              <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12, lineHeight: 1.6 }}>
                <div className="form-label" style={{ marginBottom: 4 }}>
                  Shared by {active.createdByName ?? active.sharedBy ?? '—'}
                  {active.createdByRole ? ` (${active.createdByRole})` : ''}
                  {' on '}
                  {new Date(active.sharedAt ?? active.createdAt ?? Date.now()).toLocaleString('en-GB')}
                </div>
                {active.message ? <div style={{ color: 'var(--muted)' }}>"{active.message}"</div> : null}
                {active.docNotes ? <div style={{ color: 'var(--faint)', marginTop: 4 }}>Notes: {active.docNotes}</div> : null}
              </div>
              <div className="sec-title" style={{ marginBottom: 9 }}>
                Signature Records ({active.recipients.filter((r) => r.status === 'signed').length} / {active.recipients.length})
              </div>
              {active.recipients.length === 0 ? (
                <div style={{ color: 'var(--muted)', fontSize: 11 }}>No recipients</div>
              ) : active.recipients.map((r) => {
                const isSigned = r.status === 'signed';
                const w = r.workerId ? lookupWorker(r.workerId) : null;
                const rawSig = r.signatureImageUrl ?? r.signatureData ?? null;
                // Skip pre-seeded placeholder data-URLs that decode to <50 raw bytes — they render as a broken icon.
                const sigImg = rawSig && (rawSig.startsWith('http') || (rawSig.startsWith('data:') && rawSig.length > 100)) ? rawSig : null;
                return (
                  <div key={r.id} style={{ background: 'var(--card)', border: `1px solid ${isSigned ? 'rgba(34,197,94,.3)' : 'var(--border)'}`, borderRadius: 9, padding: 11, marginBottom: 7 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      {avEl({ name: w?.name ?? r.workerName, photo: w?.photo ?? null }, 32, 12)}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{w?.name ?? r.workerName} <span style={{ fontSize: 9, color: 'var(--muted)', fontWeight: 400 }}>· {typeLabel[r.recipientType ?? 'worker']}</span></div>
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                          {w?.role ? `${w.role} · ` : ''}{r.notifiedAt ? `emailed ${fmtWhen(r.notifiedAt)}` : 'not emailed'}{r.viewedAt ? ` · opened ${fmtWhen(r.viewedAt)}` : ''}
                        </div>
                        {r.status === 'declined' && r.declineReason ? <div style={{ fontSize: 10, color: 'var(--danger)', marginTop: 2 }}>Declined: "{r.declineReason}"</div> : null}
                      </div>
                      {isSigned ? (
                        <div style={{ textAlign: 'right' }}>
                          <span className="sign-chip-signed">✓ SIGNED</span>
                          {r.signedAt ? <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 3 }}>{new Date(r.signedAt).toLocaleString('en-GB')}</div> : null}
                        </div>
                      ) : (
                        <span className="sign-chip-pending">{r.status === 'declined' ? '✕ DECLINED' : r.status === 'viewed' ? '👁 OPENED' : '⏱ PENDING'}</span>
                      )}
                    </div>
                    {isSigned && r.signedPdfUrl ? (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <a className="btn btn-ghost btn-xs" href={r.signedPdfUrl}>⬇ Signed copy{r.sealedAt ? ' (sealed' + (r.timestamped ? ', timestamped)' : ')') : ''}</a>
                        {r.verifiedVia ? <span style={{ fontSize: 9, color: 'var(--muted)' }}>{r.verifiedVia}</span> : null}
                        {r.signedPdfSha256 ? <span title={`SHA-256 ${r.signedPdfSha256}`} style={{ fontSize: 9, color: 'var(--faint)', fontFamily: 'monospace' }}>{r.signedPdfSha256.slice(0, 12)}…</span> : null}
                      </div>
                    ) : null}
                    {isAdmin && !isSigned ? (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        <button className="btn btn-ghost btn-xs" onClick={() => remind.mutate({ docId: active.id, recipientId: r.id })} disabled={remind.isPending}>✉ Remind</button>
                        <button className="btn btn-ghost btn-xs" onClick={() => copyLink(active.id, r.id)}>🔗 Copy signing link</button>
                      </div>
                    ) : null}
                    {sigImg ? (
                      <div style={{ marginTop: 8, background: '#fff', borderRadius: 6, padding: 4 }}>
                        <img src={sigImg} alt={`Signature of ${w?.name ?? r.workerName}`} style={{ maxHeight: 50, width: 'auto' }} />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </>
          ) : (
            <>
              <div className="sec-title" style={{ marginBottom: 10 }}>Signature Records</div>
              <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 30 }}>Select a document to view signatures</div>
            </>
          )}
        </div>
      </div>
      {shareOpen ? <ShareDocModal onClose={() => setShareOpen(false)} /> : null}
      {viewer ? <DocViewerModal onClose={() => setViewer(null)} url={viewer.url} name={viewer.name} subtitle={viewer.subtitle} /> : null}
      {signOpen && active ? <WorkerSignModal onClose={() => setSignOpen(false)} active={active} workers={workers} /> : null}
    </div>
  );
}
